import React, { useState } from "react";
import { Link, useLocation, useParams, matchPath } from "react-router-dom";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    Menu,
    MenuItem,
    Avatar,
    Stack,
    Button,
    Tooltip
} from "@mui/material";

import { Logout, AutoStories, AdminPanelSettingsOutlined } from "@mui/icons-material";

// Logos
import CloudContentLogoWithText from "../../../assets/images/cc_logo_only.png";

// Icons
import MediaIcon from "../../../components/icons/MediaIcon";
import SettingsIcon from "../../../components/icons/SettingsIcon";
import ContentIcon from "../../../components/icons/ContentIcon";
import ContentModelIcon from "../../../components/icons/ContentModelIcon";
import WorkspaceIcon from "../../../components/icons/WorkspaceIcon";
import DashboardIcon from "../../../components/icons/DashboardIcon";
import AppsIcon from "../../../components/icons/AppsIcon";
import KeyIcon from "../../../components/icons/KeyIcon";
import ProfileIcon from "../../../assets/images/profile_circle.png";

import AuthLogout from "../../../pages/authentication/auth-forms/AuthLogout";
import EntryQuickSwitcher from "./EntryQuickSwitcher";
import { usePlatformAdmin } from "../../../utils/platformAdmin";
import EnvironmentSwitcher from "./EnvironmentSwitcher";

const TopBar = () => {
    const { workspaceId } = useParams();
    const location = useLocation();
    const theme = useTheme();

    const currentUser = JSON.parse(localStorage.getItem('userInfo'));
    const name = currentUser?.name ? currentUser?.name?.split(' ')[0] : '';
    const avatarUrl = currentUser?.avatarUrl || ProfileIcon;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const isPlatformAdmin = usePlatformAdmin();

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Check if we are on the Edit Entry page
    const isEditEntryPage = matchPath(
        { path: "/:workspaceId/edit-entry/:id", end: true },
        location.pathname
    );

    // Navigation Items. `match` lists the extra path prefixes that should keep
    // the item highlighted — the entry editor is still "Content", for example.
    const menuItems = [
        {
            text: "Dashboard",
            icon: <DashboardIcon />,
            path: `/${workspaceId}/dashboard`,
        },
        {
            text: "Content model",
            icon: <ContentModelIcon />,
            path: `/${workspaceId}/content-models`,
        },
        {
            text: "Content",
            icon: <ContentIcon />,
            path: `/${workspaceId}/entries`,
            match: [`/${workspaceId}/edit-entry`, `/${workspaceId}/create-entry`],
        },
        {
            text: "Media",
            icon: <MediaIcon />,
            path: `/${workspaceId}/media-list`,
        },
        {
            text: "Settings",
            icon: <SettingsIcon />,
            path: `/${workspaceId}/settings`,
        },
    ];

    const primaryMenuItems = [
        {
            text: "Workspace",
            icon: <WorkspaceIcon />,
            path: `/space`,
        },
        {
            text: "Apps",
            icon: <AppsIcon />,
            path: `/apps`,
        },
        {
            text: "OAuth Clients",
            icon: <KeyIcon />,
            path: `/oauth-clients`,
        },
        {
            text: "Documentation",
            icon: <AutoStories />,
            path: `/documentation`,
        },
        ...(isPlatformAdmin
            ? [{ text: "Admin", icon: <AdminPanelSettingsOutlined />, path: `/admin` }]
            : []),
    ];

    // Determine which menu to show
    const isPrimaryView = ['/space', '/apps', '/oauth-clients', '/documentation', '/admin'].includes(location.pathname);
    const currentMenuItems = isPrimaryView ? primaryMenuItems : menuItems;

    // The quick switcher eats ~380px of the bar, so the nav needs a wider
    // viewport before it can afford to show labels next to every icon.
    const labelBreakpoint = isEditEntryPage ? 'xl' : 'lg';
    const showLabels = useMediaQuery(theme.breakpoints.up(labelBreakpoint));

    const isItemActive = (item) => {
        const prefixes = [item.path, ...(item.match || [])];
        return prefixes.some(
            (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
        );
    };

    const navButtonSx = (isSelected) => ({
        textTransform: 'none',
        flexShrink: 0,
        minWidth: showLabels ? 'auto' : 44,
        color: isSelected ? "#4f46e5" : "#64748b",
        backgroundColor: isSelected ? "rgba(99, 102, 241, 0.08)" : "transparent",
        borderRadius: '12px',
        px: showLabels ? 2 : 1.25,
        py: 1,
        fontWeight: isSelected ? 600 : 500,
        whiteSpace: 'nowrap',
        '& .MuiButton-startIcon': { mr: 1 },
        '&:hover': {
            backgroundColor: isSelected ? "rgba(99, 102, 241, 0.12)" : "rgba(241, 245, 249, 0.8)",
            color: isSelected ? "#4f46e5" : "#1e293b",
        }
    });

    // The icons draw in currentColor, so tinting the wrapper is what colours
    // them; each glyph is cloned at the bar's 20px size.
    const renderIcon = (item, isSelected) => (
        <Box
            component="span"
            sx={{
                color: isSelected ? "#4f46e5" : "#64748b",
                display: 'flex',
            }}
        >
            {React.cloneElement(item.icon, { size: 20 })}
        </Box>
    );

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                width: '100%',
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                zIndex: theme.zIndex.appBar,
                color: '#000'
            }}
        >
            <Toolbar sx={{ gap: 2, minHeight: '70px !important', overflow: 'hidden' }}>
                {/* Left: Logo + environment */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <img
                        src={CloudContentLogoWithText}
                        alt="Cloud Content"
                        style={{ height: '40px', objectFit: 'contain' }}
                    />
                    {/* Environments belong to a workspace, so the switcher only
                        appears once the user is inside one. */}
                    {!isPrimaryView && workspaceId && <EnvironmentSwitcher />}
                </Box>

                {/* Center: Navigation */}
                <Box
                    component="nav"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexGrow: 1,
                        justifyContent: 'center',
                        minWidth: 0,
                    }}
                >
                    {currentMenuItems.map((item) => {
                        const isSelected = isItemActive(item);
                        const button = showLabels ? (
                            <Button
                                key={item.text}
                                component={Link}
                                to={item.path}
                                startIcon={renderIcon(item, isSelected)}
                                sx={navButtonSx(isSelected)}
                            >
                                {item.text}
                            </Button>
                        ) : (
                            <Tooltip key={item.text} title={item.text} arrow>
                                <Button
                                    component={Link}
                                    to={item.path}
                                    aria-label={item.text}
                                    sx={navButtonSx(isSelected)}
                                >
                                    {renderIcon(item, isSelected)}
                                </Button>
                            </Tooltip>
                        );
                        return button;
                    })}
                </Box>

                {/* Right: Quick switcher, workspaces, user profile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {/* Quick Switcher for Edit Entry Page */}
                    {isEditEntryPage && <EntryQuickSwitcher />}

                    {/* Back to Workspaces (only when not already in primary view) */}
                    {!isPrimaryView && (
                        <Tooltip title="Workspaces" arrow>
                            <Button
                                component={Link}
                                to="/space"
                                aria-label="Workspaces"
                                sx={{
                                    minWidth: 44,
                                    flexShrink: 0,
                                    color: "#64748b",
                                    borderRadius: '12px',
                                    px: 1.25,
                                    py: 1,
                                    '&:hover': {
                                        backgroundColor: "rgba(241, 245, 249, 0.8)",
                                        color: "#1e293b",
                                    }
                                }}
                            >
                                <WorkspaceIcon size={20} />
                            </Button>
                        </Tooltip>
                    )}

                    <Box sx={{ width: '1px', height: '24px', bgcolor: 'divider', flexShrink: 0 }} />

                    <Box
                        onClick={handleMenuClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexShrink: 0,
                            cursor: 'pointer',
                            p: 0.5,
                            pr: { xs: 0.5, md: 1.5 },
                            borderRadius: '24px',
                            border: '1px solid transparent',
                            '&:hover': {
                                backgroundColor: "rgba(241, 245, 249, 0.8)",
                                borderColor: "rgba(226, 232, 240, 0.8)"
                            }
                        }}
                    >
                        <Avatar
                            alt={name}
                            src={avatarUrl}
                            sx={{ width: 36, height: 36 }}
                        />
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 120 }}
                        >
                            {name}
                        </Typography>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                                mt: 1.5,
                                borderRadius: '12px',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        {isPlatformAdmin && (
                            <MenuItem component={Link} to="/admin" onClick={handleMenuClose}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <AdminPanelSettingsOutlined fontSize="small" />
                                    <Typography variant="body2">Admin console</Typography>
                                </Stack>
                            </MenuItem>
                        )}
                        <MenuItem disableRipple>
                            <AuthLogout>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Logout fontSize="small" />
                                    <Typography variant="body2">Log out</Typography>
                                </Stack>
                            </AuthLogout>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;
